import React, { useCallback } from 'react';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import './custom-pagination.scss';

interface PaginationProps {
  rowCount?: number;
  totalCount?: number;
  onChangeRow?: (rowsnumber: number) => unknown;
  currentPage?: number;
  selfPaginatedElement?: React.ReactNode;
}

const pageCount: Record<string, number> = {
  Eight: 8,
  Sixteen: 16,
  Thirty_Two: 32,
  Fifty_Six: 56,
};

const rowsCount = Object.keys(pageCount).map((key) => pageCount[key]);

const formatNumber = (number: number) => {
  return number.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const getViewStatement = (rowCount: number, currentPage: number, totalCount: number) => {
  const endIndex = rowCount * currentPage;
  const startIndex = rowCount * currentPage - rowCount + 1;
  const formattedTotalCount = formatNumber(totalCount);

  return (
    <>
      Viewing {formatNumber(startIndex)}-{endIndex > totalCount ? formattedTotalCount : formatNumber(endIndex)} of{' '}
      {formattedTotalCount}
    </>
  );
};

export const CustomPagination = ({
  rowCount,
  totalCount,
  onChangeRow,
  currentPage,
  selfPaginatedElement,
}: PaginationProps) => {
  const onDropdownRowChange = (rowCount: number) => {
    onChangeRow && onChangeRow(rowCount);
  };

  const getMaxCount = (totalCount: number) => {
    let maxValue = 0;
    if (totalCount <= 8) maxValue = 8;
    else if (totalCount <= 16) maxValue = 16;
    else if (totalCount <= 32) maxValue = 32;
    else maxValue = 56;
    return maxValue;
  };

  const handleRowsPerPage = useCallback((row: number, onDropdownRowChange: (rowCount: number) => unknown) => {
    return (
      <span className="rowDropdown-wrapper">
        <span className="rowDropdown-detail">Show:</span>
        <DropdownMenu shouldFitContainer trigger={row} triggerType="button" testId="dropdown-for-rows">
          <DropdownItemGroup>
            {rowsCount.map((count, index) =>
              totalCount && count <= getMaxCount(totalCount) ? (
                <DropdownItem key={index} onClick={() => onDropdownRowChange(count)}>
                  {count}
                </DropdownItem>
              ) : null
            )}
          </DropdownItemGroup>
        </DropdownMenu>
      </span>
    );
  }, []);

  return (
    <div className="custom-pagination-wrapper" data-testid="custom-pagination-details">
      <span className="custom-pagination-rows-detail">
        {totalCount && rowCount && currentPage && getViewStatement(rowCount, currentPage, totalCount)}
      </span>
      {selfPaginatedElement && <>{selfPaginatedElement}</>}
      {rowCount && handleRowsPerPage(rowCount, onDropdownRowChange)}
    </div>
  );
};
